// What is logged at this place, below the map on its EDIT page.
//
// The same two tables /me/places/:id/visits shows (PlaceRecordsCard is shared),
// but here rather than one click away, because the question belongs to the
// edit. "Is this the Cap Sante forty entries refer to, or the empty duplicate?"
// is what a rename or a delete needs answered BEFORE it is made, and the
// curator asking it is on this page.
//
// BEHIND A BUTTON, AND THAT IS THE DESIGN. There is no place-keyed query and
// there cannot be one -- a waypoint's binding lives inside an AWSJSON blob,
// which DynamoDB can't index -- so the answer costs a walk of the whole fleet
// (1 + 3N paged queries; usePlaceRecords says so at length). Opening this page
// to fix a typo must not pay that. So the gate is a MOUNT gate: while it is
// collapsed no data hook exists, which is a stronger guarantee than an
// `enabled` flag and is what lets every existing test of this page keep its
// mocks. Nothing below PlaceVisitsSection's own state may read the network.
//
// TWO CONSEQUENCES WORTH KNOWING:
//
//   Clicking the button is the right moment to flush. usePlaceRecords runs
//   saveQueue.flushAll() and waits out the place:/trip:/entry: lanes before its
//   first await, because place.nameKey IS the match key. On this page the user
//   may have a rename still sitting in the typing debounce; the click flushes
//   it and the fan-out matches against the new name. The gate makes that
//   ordering exact rather than lucky.
//
//   The list is a snapshot, and Refresh is what makes that honest. The hook's
//   deps are [placeId, placeIndex], so a rename AFTER the fan-out doesn't
//   rebuild it -- and re-running on every keystroke of the Name box is exactly
//   what this whole file exists to avoid. Refresh bumps `generation`, which
//   remounts the loader and re-runs everything; savePlaceEdit has already
//   invalidated the module cache behind usePlaceIndex, so the rebuilt index
//   sees the new name.

import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { DataCard } from '../../components';
import { CardHeader } from '../../components/CardHeader';
import { useRootCrumbNavigate } from '../item/RootCrumb';
import { urlForPlaceVisits } from '../../fleet-shared/Urls.mjs';
import { usePlaceIndex } from '../../utilities/usePlaceIndex';
import { usePlaceRecords } from './usePlaceRecords';
import { PlaceRecordFailures, PlaceRecordsCard, placeRecordUrl } from './PlaceRecordsCard';

const HEADING = 'Visits';

// Mounted only once the user has asked. Everything that reads is in here.
const PlaceVisitsLoader = ({ placeId, onRefresh }) => {
  const navigate = useRootCrumbNavigate();
  const placeIndex = usePlaceIndex();
  const { place, records, failures, StatusMessage } = usePlaceRecords(placeId, placeIndex);

  const onSelect = useCallback((row) => navigate(placeRecordUrl(row)), [navigate]);

  return (
    <>
      {/* The hook's own line: "Loading...", then "Searching 3 of 14 items...".
          Unlike TripsCalendarSection there is nothing above narrating the same
          read -- the edit card is loading one row, not the fleet -- so the
          progress is worth showing. */}
      <StatusMessage />

      <PlaceRecordFailures failures={failures} />

      <PlaceRecordsCard
        place={place}
        records={records}
        placeIndex={placeIndex}
        onSelect={onSelect}
        heading={HEADING}
        // No routes map on this page, only the place's own pin. The flag's
        // tooltip is "Drawn on the map above"; showing it here would name
        // something that isn't on the page.
        showOnMap={false}
        extraActions={
          <Button variant="outline-secondary" size="sm" onClick={onRefresh}>
            Refresh
          </Button>
        }
      />
    </>
  );
};

const PlaceVisitsSection = ({ placeId }) => {
  // 0 is collapsed; every value above it is a mount key for the loader, so
  // Refresh is a remount rather than a second code path.
  const [generation, setGeneration] = useState(0);

  if (generation === 0) {
    return (
      <DataCard
        header={<CardHeader text={HEADING} />}
        body={
          <div className="text-center py-2">
            <Button variant="outline-secondary" onClick={() => setGeneration(1)}>
              Show visits
            </Button>
            <p className="text-body-secondary small mb-0 mt-2">
              There is no index for this, so finding it means reading every item&apos;s trips
              and logbook entries.{' '}
              <Link to={urlForPlaceVisits(placeId)}>Open the full page</Link> for these over a
              map of their routes.
            </p>
          </div>
        }
      />
    );
  }

  return (
    <PlaceVisitsLoader
      key={generation}
      placeId={placeId}
      onRefresh={() => setGeneration((current) => current + 1)}
    />
  );
};

export default PlaceVisitsSection;
